import { defineStore } from "pinia";
import db from "@/db";
import {
  getOrderedSamples,
  getLastSample,
  getFirstSample,
  isSampleMainLine,
} from "@/store/utils/sampleUtils";
import { v4 as uuidv4 } from "uuid";

import crudGenerator from "@/api/utils/offlineSync";
import { useProjectsStore } from "@/store/modules/projects";
import { useInstallationsStore } from "@/store/modules/installations";
import { useSampleFlaskPrepsStore } from "@/store/modules/sampleFlaskPrep";

const {
  innerFetch,
  innerCreate,
  innerPartialUpdate,
  innerDelete,
  innerBulkCreate,
} = crudGenerator(db.models.Sample);

export const useSamplesStore = defineStore("samples", {
  state: () => ({
    sampleList: [],
    sampleIsLoading: false,
  }),
  actions: {
    async fetchSamples(projectUuid) {
      return innerFetch(
        { project: projectUuid },
        useProjectsStore().isOfflineState,
        (isLoading) => {
          this.sampleIsLoading = isLoading;
        }
      );
    },

    async filterSamplesByPhasesUuids(phasesUuids) {
      while (this.sampleIsLoading) await new Promise((r) => setTimeout(r, 200));

      this.sampleIsLoading = true;

      try {
        const samples = await db.samples
          .where("phase")
          .anyOf(phasesUuids)
          .toArray();

        for (const sample of samples) {
          await sample.setRelationships();
        }

        this.sampleList = [...samples];

        return samples;
      } finally {
        this.sampleIsLoading = false;
      }
    },

    async buildSampleChain(phaseUuid) {
      const samples = await getOrderedSamples(phaseUuid);
      await useInstallationsStore().refreshLastUpdated();

      this.replaceSamplesOfPhase(samples, phaseUuid);

      return samples;
    },

    async createSample({ form }) {
      const isOfflineState = useProjectsStore().isOfflineState;
      let firstSample;
      if (form.previousSample === undefined) {
        if (await isSampleMainLine(form)) {
          firstSample = await getFirstSample(form.phase);
          form.previousSample = null;
        } else {
          const lastSample = await getLastSample(form.phase);
          form.previousSample = lastSample ? lastSample.uuid : null;
        }
      }
      const sample = await innerCreate(form, isOfflineState, {
        requiresNetwork: true,
      });

      await sample.setRelationships();
      useSampleFlaskPrepsStore().createFromSample(sample);

      if (firstSample) {
        await innerPartialUpdate(
          { uuid: firstSample.uuid, previousSample: sample.uuid },
          isOfflineState
        );
      }

      await useInstallationsStore().refreshLastUpdated();
      const samples = await db.samples
        .where("phase")
        .equals(sample.phase)
        .toArray();
      const sampleObjects = await Promise.all(
        samples.map(async (phaseSample) => {
          await phaseSample.setRelationships();
          return phaseSample;
        })
      );
      this.replaceSamplesOfPhase(sampleObjects, sample.phase);

      return sample;
    },

    async updateSample(form) {
      const sample = await innerPartialUpdate(
        form,
        useProjectsStore().isOfflineState
      );
      await useInstallationsStore().refreshLastUpdated();

      this.replaceSample(sample);

      return sample;
    },

    async deleteSample(sample) {
      const projectsStore = useProjectsStore();
      const nextSample = await db.samples.get({ previousSample: sample.uuid });

      await innerDelete(
        sample.uuid,
        projectsStore.isOfflineState,
        projectsStore.currentProjectUuid
      );

      // INFO - 18-06-2025 - cascade delete sampleFlaskPreps in back
      await db.sampleFlaskPreps.where("sample").equals(sample.uuid).delete();

      if (nextSample) {
        nextSample.previousSample = sample.previousSample;
        await innerPartialUpdate(
          { uuid: nextSample.uuid, previousSample: sample.previousSample },
          projectsStore.isOfflineState
        );
        await nextSample.setRelationships();
        this.replaceSample(nextSample);
      }

      await useInstallationsStore().refreshLastUpdated();

      this.sampleList = this.sampleList.filter(
        ({ uuid }) => uuid !== sample.uuid
      );
    },

    async removeSampleByPhase(phaseUuid) {
      await db.samples.where({ phase: phaseUuid }).delete();
      await db.sampleFlaskPreps.where("phase").equals(phaseUuid).delete();
      this.sampleList = this.sampleList.filter(
        ({ phase }) => phase !== phaseUuid
      );
    },

    async removeSampleByPhases(phasesUuids) {
      await db.samples.where("phase").anyOf(phasesUuids).delete();
      await db.sampleFlaskPreps.where("phase").anyOf(phasesUuids).delete();
      this.sampleList = [];
    },

    async bulkCreateOrUpdateSample({ phaseUuid, samples }) {
      const form = [];
      let previousUuid = null;
      for (const sample of samples) {
        if (!sample.uuid) {
          sample.uuid = uuidv4();
        }
        sample.previousSample = previousUuid;
        sample.phase = phaseUuid;
        form.push(sample);
        previousUuid = sample.uuid;
      }
      const newSamples = await innerBulkCreate(
        form,
        useProjectsStore().isOfflineState,
        { requiresNetwork: true }
      );
      for (const sample of newSamples) {
        await sample.setRelationships();
      }
      useSampleFlaskPrepsStore().bulkCreateFromSample(newSamples);
      this.replaceSamplesOfPhase(newSamples, phaseUuid);
    },

    replaceSample(newSample) {
      const index = this.sampleList.findIndex(
        ({ uuid }) => newSample.uuid === uuid
      );

      if (index < 0) return;

      this.sampleList[index] = newSample;
      this.sampleList = [...this.sampleList];
    },

    replaceSamplesOfPhase(samples, phaseUuid) {
      this.sampleList = [
        ...this.sampleList.filter(({ phase }) => phase !== phaseUuid),
        ...samples,
      ];
    },
  },
});
